import { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { Input, InputChangeEvent } from "@progress/kendo-react-inputs";
import { FiSearch } from "react-icons/fi";
import { IoCloseOutline } from "react-icons/io5";
import SpinbuttonDateInput from "@/components/shared/forms/SpinbuttonDateInput";

type PreschoolSummary = {
  PreschoolCDSCode: string;
  PreschoolName: string;
};

export type StaffSearchFormState = {
  staffLocalId: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
};

type StaffWizardSearchFormProps = {
  preschools: PreschoolSummary[];
  disabled: boolean;
  onSearch: (filters: StaffSearchFormState) => void;
  onCancel: () => void;
  searchError?: string;
  onClearSearchError?: () => void;
  searchCriteria?: StaffSearchFormState | null;
};

export type StaffSearchFormRef = {
  handleSearch: () => void;
  handleCancel: () => void;
};

export const StaffWizardSearchForm = forwardRef<
  StaffSearchFormRef,
  StaffWizardSearchFormProps
>(function StaffWizardSearchForm(
  {
    disabled,
    onSearch,
    onCancel,
    searchError,
    onClearSearchError,
    searchCriteria,
  },
  ref
) {
  const [formState, setFormState] = useState<StaffSearchFormState>({
    staffLocalId: searchCriteria?.staffLocalId || "",
    firstName: searchCriteria?.firstName || "",
    lastName: searchCriteria?.lastName || "",
    dateOfBirth: searchCriteria?.dateOfBirth || "",
  });
  const [validationError, setValidationError] = useState<string>("");

  const [lastSearchOption, setLastSearchOption] = useState<
    "option1" | "option2" | null
  >(null);
  const [searchedStaffLocalId, setSearchedStaffLocalId] = useState<string>("");
  const [dobResetKey, setDobResetKey] = useState(0);
  const [dobIncomplete, setDobIncomplete] = useState(false);

  const option1Active = formState.staffLocalId.trim().length > 0;
  const option2Active =
    formState.firstName.trim().length > 0 ||
    formState.lastName.trim().length > 0 ||
    formState.dateOfBirth.trim().length > 0 ||
    dobIncomplete;

  useEffect(() => {
    if (searchCriteria) {
      setFormState({
        staffLocalId: searchCriteria.staffLocalId || "",
        firstName: searchCriteria.firstName || "",
        lastName: searchCriteria.lastName || "",
        dateOfBirth: searchCriteria.dateOfBirth || "",
      });
      setDobResetKey((k) => k + 1);
      setDobIncomplete(false);
    }
  }, [searchCriteria]);

  const handleChange = (key: keyof StaffSearchFormState, value: string) => {
    setFormState((prev) => ({
      ...prev,
      [key]: value,
    }));
    if (validationError) {
      setValidationError("");
    }
    if (searchError && onClearSearchError) {
      onClearSearchError();
    }
  };

  const handleClearOption1 = () => {
    setFormState((prev) => ({
      ...prev,
      staffLocalId: "",
    }));
    if (validationError) {
      setValidationError("");
    }
    if (searchError && onClearSearchError) {
      onClearSearchError();
    }
  };

  const handleClearOption2 = () => {
    setFormState((prev) => ({
      ...prev,
      firstName: "",
      lastName: "",
      dateOfBirth: "",
    }));
    setDobResetKey((k) => k + 1);
    setDobIncomplete(false);
    if (validationError) {
      setValidationError("");
    }
    if (searchError && onClearSearchError) {
      onClearSearchError();
    }
  };

  const validateForm = (): boolean => {
    const hasStaffLocalId = formState.staffLocalId.trim().length > 0;
    const hasFirstName = formState.firstName.trim().length > 0;
    const hasLastName = formState.lastName.trim().length > 0;
    const hasDOB = formState.dateOfBirth.trim().length === 10;

    if (hasStaffLocalId) {
      return true;
    }

    if (hasFirstName && hasLastName && hasDOB) {
      const dobValue = formState.dateOfBirth.trim();
      const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
      const mdyDateRegex = /^\d{2}\/\d{2}\/\d{4}$/;

      const parseDob = (value: string): Date | null => {
        if (isoDateRegex.test(value)) {
          const [yStr, mStr, dStr] = value.split("-");
          const y = Number(yStr);
          const m = Number(mStr);
          const d = Number(dStr);
          if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) {
            return null;
          }
          const parsed = new Date(Date.UTC(y, m - 1, d));
          if (
            parsed.getUTCFullYear() !== y ||
            parsed.getUTCMonth() + 1 !== m ||
            parsed.getUTCDate() !== d
          ) {
            return null;
          }
          return parsed;
        }

        if (mdyDateRegex.test(value)) {
          const [mStr, dStr, yStr] = value.split("/");
          const y = Number(yStr);
          const m = Number(mStr);
          const d = Number(dStr);
          if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) {
            return null;
          }
          const parsed = new Date(Date.UTC(y, m - 1, d));
          if (
            parsed.getUTCFullYear() !== y ||
            parsed.getUTCMonth() + 1 !== m ||
            parsed.getUTCDate() !== d
          ) {
            return null;
          }
          return parsed;
        }

        return null;
      };

      const parsedDob = parseDob(dobValue);
      if (!parsedDob) {
        setValidationError("Please enter a valid Date of Birth.");
        return false;
      }

      const minDate = new Date("1900-01-01");
      if (parsedDob.getTime() <= minDate.getTime()) {
        setValidationError("Please enter a valid Date of Birth.");
        return false;
      }

      const now = new Date();
      const todayUtc = new Date(
        Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
      );
      if (parsedDob.getTime() > todayUtc.getTime()) {
        setValidationError("Please enter a valid Date of Birth.");
        return false;
      }

      return true;
    }

    if (
      hasFirstName ||
      hasLastName ||
      formState.dateOfBirth.trim().length > 0 ||
      dobIncomplete
    ) {
      if (dobIncomplete) {
        setValidationError(
          "Please enter a complete Date of Birth (MM/DD/YYYY)."
        );
      } else {
        setValidationError(
          "Please enter First Name, Last Name, and Date of Birth together."
        );
      }
      return false;
    }

    setValidationError(
      "Please enter either Staff Local ID or First Name, Last Name, and Date of Birth."
    );
    return false;
  };

  const handleSearchClick = () => {
    if (!disabled && validateForm()) {
      if (formState.staffLocalId.trim().length > 0) {
        setLastSearchOption("option1");
        setSearchedStaffLocalId(formState.staffLocalId.trim());
      } else {
        setLastSearchOption("option2");
        setSearchedStaffLocalId("");
      }
      onSearch(formState);
    }
  };

  const handleCancelClick = () => {
    onCancel();
  };

  useImperativeHandle(ref, () => ({
    handleSearch: handleSearchClick,
    handleCancel: handleCancelClick,
  }));

  return (
    <div style={{ padding: "0 24px 24px", background: "white" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "24px",
          gap: "12px",
        }}
      >
        <div
          style={{
            width: "40px",
            height: "40px",
            background: "#1e5a78",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <FiSearch size={18} color="white" />
        </div>
        <p
          style={{
            margin: 0,
            fontSize: "14px",
            color: "#666",
            lineHeight: "1.5",
          }}
        >
          Search for a staff record match already in the CAPSDAC system.
        </p>
      </div>

      {/* Option 1: Staff Local ID */}
      <div
        style={{
          marginBottom: "24px",
          padding: "16px",
          background: option1Active ? "#f8f9fa" : "transparent",
          borderLeft: option1Active
            ? "4px solid #1e5a78"
            : "4px solid transparent",
          borderRadius: "4px",
          opacity: option2Active ? 0.3 : 1,
          pointerEvents: option2Active ? "none" : "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "8px",
          }}
        >
          <span
            style={{
              fontSize: "13px",
              color: "#666",
              fontWeight: "500",
            }}
          >
            Option 1
          </span>
          {option1Active && (
            <button
              type="button"
              onClick={handleClearOption1}
              disabled={disabled}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                background: "none",
                border: "none",
                color: "#666",
                fontSize: "13px",
                cursor: "pointer",
                padding: "4px",
              }}
            >
              <IoCloseOutline size={16} />
              Clear
            </button>
          )}
        </div>

        <label
          htmlFor="staff-wizard-local-id"
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "8px",
            fontSize: "14px",
            color: "#333",
            gap: "6px",
          }}
        >
          Enter Staff Local ID
          <FiSearch size={14} color="#6c757d" aria-hidden="true" />
        </label>
        <Input
          id="staff-wizard-local-id"
          style={{
            width: "100%",
            maxWidth: "340px",
            border: option1Active ? "2px solid #1e5a78" : undefined,
          }}
          value={formState.staffLocalId}
          onChange={(e: InputChangeEvent) =>
            handleChange("staffLocalId", e.value || "")
          }
          placeholder="Enter Staff Local ID"
          disabled={disabled || option2Active}
        />

        {/* Search error for Staff Local ID (Option 1) */}
        {searchError && lastSearchOption === "option1" && (
          <div
            style={{
              padding: "12px 16px",
              background: "#f8d7da",
              border: "1px solid #f5c2c7",
              borderRadius: "4px",
              marginTop: "12px",
              fontSize: "14px",
              color: "#842029",
            }}
          >
            No matches found for Staff Local ID &quot;{searchedStaffLocalId}
            &quot;. Please search again using First Name, Last Name, and Date of
            Birth.
          </div>
        )}
      </div>

      {/* OR Divider */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          margin: "32px 0",
          gap: "16px",
        }}
      >
        <div
          style={{
            flex: 1,
            height: "1px",
            background: "#ccc",
          }}
        />
        <span
          style={{
            color: "#6c757d",
            fontSize: "14px",
            fontWeight: 500,
          }}
        >
          OR
        </span>
        <div
          style={{
            flex: 1,
            height: "1px",
            background: "#ccc",
          }}
        />
      </div>

      {/* Option 2: Name and DOB */}
      <div
        style={{
          marginBottom: "24px",
          padding: "16px",
          background: option2Active ? "#f8f9fa" : "transparent",
          borderLeft: option2Active
            ? "4px solid #1e5a78"
            : "4px solid transparent",
          borderRadius: "4px",
          opacity: option1Active ? 0.3 : 1,
          pointerEvents: option1Active ? "none" : "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "8px",
          }}
        >
          {/* Section heading — not a control label, so use a span */}
          <span
            style={{
              fontSize: "13px",
              color: "#666",
              fontWeight: "500",
            }}
          >
            Option 2
          </span>
          {option2Active && (
            <button
              type="button"
              onClick={handleClearOption2}
              disabled={disabled}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                background: "none",
                border: "none",
                color: "#666",
                fontSize: "13px",
                cursor: "pointer",
                padding: "4px",
              }}
            >
              <IoCloseOutline size={16} />
              Clear
            </button>
          )}
        </div>

        {/* Group heading for the three fields below — not a single-control label */}
        <span
          style={{
            display: "block",
            marginBottom: "16px",
            fontSize: "14px",
            color: "#333",
          }}
        >
          Enter First Name, Last Name, Date of Birth
        </span>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "16px",
          }}
        >
          <div>
            <label
              htmlFor="staff-wizard-first-name"
              style={{
                display: "block",
                marginBottom: "6px",
                fontSize: "13px",
                color: "#666",
              }}
            >
              First Name
            </label>
            <Input
              id="staff-wizard-first-name"
              style={{ width: "100%" }}
              value={formState.firstName}
              onChange={(e: InputChangeEvent) =>
                handleChange("firstName", e.value || "")
              }
              placeholder="Enter first name"
              disabled={disabled || option1Active}
            />
          </div>

          <div>
            <label
              htmlFor="staff-wizard-last-name"
              style={{
                display: "block",
                marginBottom: "6px",
                fontSize: "13px",
                color: "#666",
              }}
            >
              Last Name
            </label>
            <Input
              id="staff-wizard-last-name"
              style={{ width: "100%" }}
              value={formState.lastName}
              onChange={(e: InputChangeEvent) =>
                handleChange("lastName", e.value || "")
              }
              placeholder="Enter last name"
              disabled={disabled || option1Active}
            />
          </div>

          <div>
            <label
              id="staff-wizard-dob-label"
              style={{
                display: "block",
                marginBottom: "6px",
                fontSize: "13px",
                color: "#666",
              }}
            >
              Date of Birth
              <span className="visually-hidden"> format mm/dd/yyyy</span>
            </label>
            <SpinbuttonDateInput
              key={dobResetKey}
              id="staff-wizard-dob"
              labelledBy="staff-wizard-dob-label"
              value={formState.dateOfBirth}
              min="1900-01-01"
              max={new Date().toISOString().split("T")[0]}
              onChange={(value) => handleChange("dateOfBirth", value)}
              onIncompleteChange={setDobIncomplete}
              disabled={disabled || option1Active}
            />{" "}
          </div>
        </div>

        {/* Search error for Name/DOB (Option 2) */}
        {searchError && lastSearchOption === "option2" && (
          <div
            style={{
              padding: "12px 16px",
              background: "#f8d7da",
              border: "1px solid #f5c2c7",
              borderRadius: "4px",
              marginTop: "12px",
              fontSize: "14px",
              color: "#842029",
            }}
          >
            {searchError}
          </div>
        )}
      </div>

      {validationError && (
        <div
          style={{
            padding: "12px 16px",
            background: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "4px",
            marginBottom: "16px",
            fontSize: "14px",
            color: "#856404",
          }}
        >
          {validationError}
        </div>
      )}
    </div>
  );
});
